/**
 * 마스터플랜 캘린더 ($j.F.Admin.MasterCalendar) — 두 화면이 같은 그림을 쓴다.
 *   - /adm/project/master  : 입력 화면. 막대·날짜를 누르면 작업 시트를 연다 (onOpen/onNew)
 *   - /adm/project/weekly  : 주간업무보고서 「마스터플랜」 섹션. 보기 전용
 *
 *   $j.F.Admin.MasterCalendar.mount(el, {
 *       tasks, phases, holidays, today,   // MasterPlanService::tasks()/PHASES, HolidayService::map() 모양 그대로
 *       month: '2026-10',                 // 처음 보여줄 달 (없으면 오늘 달). 다시 mount 하면 보던 달을 유지
 *       readonly: true,                   // 보고서용 — 누를 곳을 없앤다
 *       focus: ['2026-10-10','2026-10-16'], // 보고서용 — 오늘 하루 대신 보고 주차 한 줄을 강조. [이번주] 가 그 달로 돌아간다
 *       colorBy: 'project'(기본)|'phase'|'person', onColor(v), onOpen(num), onNew(date),
 *   });
 *
 * 색은 기본이 프로젝트별이다 — 줄기(원장의 상위 프로젝트, 없으면 그 프로젝트)가 같으면 한 색이다.
 * 서류개편 › 서류등록 / 서류개편 › 4단계처럼 하위가 달라도 같은 색, 단계는 막대 앞 [기획] 말머리로 읽는다.
 * 색 묶음 키(color_key)는 서버 MasterPlanService 가 정한다. 키 정렬 순서로 팔레트를 돌리므로
 * 입력 화면과 보고서가 같은 작업 묶음을 보면 같은 색이 나온다.
 *
 * 주(월~금) 단위 행. 실선 막대 = 현재 일정, 점선 = 기준 일정(바뀌었을 때만), ◆ = 하루짜리 일정.
 * is_project 막대 = 작업이 없는 프로젝트를 원장 기간으로 그린 것 ([프로젝트] 말머리, 옅은 바탕). 입력 화면만 넘긴다.
 * 막대 바탕의 진한 부분 = 진행률. 작업 기간 전체를 한 줄로 보고 그 비율만큼 앞에서부터 칠한다(여러 주면 주마다 이어진다). 완료는 칠하지 않는다.
 * 읽을 거리를 줄인다 — 완료 막대는 진행 중인 막대 아래로 모으고(흐리게 + ✓),
 *   여러 주에 걸친 막대는 시작하는 주(또는 그 달 첫 줄)에만 이름을 쓰고, 뒤로는 「← 이름」만 흐리게. 지연 표시는 끝나는 주에.
 * 주말 일정은 금요일 칸에 붙인다. 지연 일수는 영업일(서버 MasterPlanService::bizDiff 와 같은 셈).
 *
 * CSS 는 여기서 1회 주입한다 — 보고서 '프린트' 새 창에도 같은 CSS 를 실어야 해서 cssText() 로 꺼내 쓴다.
 * 새 창엔 관리자 토큰이 없으므로 토큰마다 라이트 폴백을 둔다.
 */
const DOWS = '일월화수목금토';
const pad = n => String(n).padStart(2, '0');
const iso = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = s => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const utc = s => { const [y, m, d] = s.split('-').map(Number); return Date.UTC(y, m - 1, d); };
const diff = (a, b) => Math.round((utc(b) - utc(a)) / 864e5);
const addD = (s, n) => { const d = parse(s); d.setDate(d.getDate() + n); return iso(d); };
const mn = (a, b) => (a < b ? a : b);
const mx = (a, b) => (a > b ? a : b);
const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fmt = s => { if (!s) return '-'; const d = parse(s); return `${d.getMonth() + 1}/${d.getDate()}(${DOWS[d.getDay()]})`; };
const md = s => { const d = parse(s); return `${d.getMonth() + 1}/${d.getDate()}`; };

/** 프로젝트 팔레트 — 단계색·상태색(빨강)과 헷갈리지 않게 고른 10색. 다크는 CSS 에서 밝힌다 */
const PJ_PAL = ['#1E6FD9', '#2E9E5B', '#8E44AD', '#E07B00', '#00897B', '#C2185B', '#3F51B5', '#8D6E63', '#7CA81E', '#0097A7'];
const PJ_NONE = '#9AA0A6';

/** 담당자 파스텔 — 주간보고(Weekly.js·WeeklyMarkdown)와 같은 색 */
const pastel = name => ({ '홍': ['#ECE3FB', '#6A3FA0'], '박': ['#E1ECFB', '#2F5C9E'], '유': ['#E2F3E4', '#37773F'], '윤': ['#FBEFD6', '#8A6612'] }[String(name ?? '').trim().charAt(0)] || ['#ECEEF1', '#5A6270']);
// 사람 배지는 흰 바탕 + 이름 글자색만 사람별 (막대 색 위에 얹혀도 배지끼리 바탕이 섞이지 않게)
const ob = name => `<span class="mpc-ob" style="color:${pastel(name)[1]}">${esc(name)}</span>`;

const CSS = `
.mpc { --mpc-surface: var(--adm-surface, #fff); --mpc-soft: var(--adm-surface-soft, #f6f7f9); --mpc-border: var(--adm-border, #e3e6ea);
       --mpc-border-soft: var(--adm-border-soft, #eef0f3); --mpc-border-strong: var(--adm-border-strong, #c9ced6);
       --mpc-text: var(--adm-text, #343a40); --mpc-heading: var(--adm-text-heading, #1f2329); --mpc-muted: var(--adm-text-muted, #6c757d);
       --mpc-faint: var(--adm-text-faint, #adb5bd); --mpc-danger: var(--adm-danger, #dc3545); --mpc-hover: var(--adm-row-hover, #f1f3f5);
       --mpc-r-xs: var(--adm-radius-xs, .2rem); --mpc-r-sm: var(--adm-radius-sm, .25rem);
       --mp-plan: #0288D1; --mp-dev: #E8650A; --mp-test: #B8860B; --mp-freeze: #C62828; --mp-work: #7B4FB5; --mp-open: #2E7D32; --mp-etc: #6B7280; --ph: var(--mp-etc);
       color: var(--mpc-text); font-size: 12px; line-height: 1.4; }
[data-theme="dark"] .mpc { --mp-plan: #29B6F6; --mp-dev: #FF8A3D; --mp-test: #FFD54F; --mp-freeze: #FF6B6B; --mp-work: #B38DF0; --mp-open: #66BB6A; --mp-etc: #9CA3AF; }
.mpc .ph-plan { --ph: var(--mp-plan); } .mpc .ph-dev { --ph: var(--mp-dev); } .mpc .ph-test { --ph: var(--mp-test); }
.mpc .ph-freeze { --ph: var(--mp-freeze); } .mpc .ph-work { --ph: var(--mp-work); } .mpc .ph-open { --ph: var(--mp-open); } .mpc .ph-etc { --ph: var(--mp-etc); }
.mpc button { font: inherit; cursor: pointer; }
.mpc.is-ro button { cursor: default; }
.mpc-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 10px 12px; border-bottom: 1px solid var(--mpc-border); }
.mpc-nav { display: inline-flex; border: 1px solid var(--mpc-border-strong); border-radius: var(--mpc-r-sm); overflow: hidden; }
.mpc-nav button { border: 0; background: var(--mpc-surface); color: var(--mpc-text); padding: 3px 10px; font-size: 12.5px; line-height: 1.5; }
.mpc-nav button + button { border-left: 1px solid var(--mpc-border-strong); }
.mpc-nav button:hover { background: var(--mpc-hover); }
.mpc-title { margin: 0; min-width: 104px; text-align: center; font-weight: 700; font-size: 16px; color: var(--mpc-heading); }
.mpc-mode button { border: 1px solid var(--mpc-border-strong); background: var(--mpc-surface); color: var(--mpc-text); padding: 3px 10px; font-size: 12.5px; line-height: 1.5; }
.mpc-mode button:first-child { border-radius: var(--mpc-r-sm) 0 0 var(--mpc-r-sm); }
.mpc-mode button + button { border-left: 0; }
.mpc-mode button:last-child { border-radius: 0 var(--mpc-r-sm) var(--mpc-r-sm) 0; }
.mpc-mode button.on { background: var(--mpc-muted); color: #fff; border-color: var(--mpc-muted); }
.mpc-legend { display: flex; flex-wrap: wrap; gap: 3px 12px; color: var(--mpc-muted); margin-left: auto; }
.mpc-legend span { display: inline-flex; align-items: center; gap: 5px; }
.mpc-legend i { display: inline-block; width: 12px; height: 12px; border-radius: var(--mpc-r-xs); background: color-mix(in srgb, var(--ph) 22%, var(--mpc-surface)); border-left: 3px solid var(--ph); }
.mpc-legend i.ms { width: 9px; height: 9px; transform: rotate(45deg); background: var(--ph); border: 0; }
.mpc-legend i.base { width: 20px; height: 0; border: 0; border-top: 2px dashed var(--mpc-faint); background: none; border-radius: 0; }
.mpc-scroll { overflow-x: auto; }
.mpc-cal { min-width: 760px; }
.mpc-dow { display: grid; grid-template-columns: repeat(5, 1fr); border-bottom: 1px solid var(--mpc-border); }
.mpc-dow div { padding: 5px 8px; font-weight: 600; color: var(--mpc-muted); }
.mpc-week { position: relative; border-bottom: 1px solid var(--mpc-border); min-height: 96px; }
.mpc-week:last-child { border-bottom: 0; }
.mpc-bg { position: absolute; inset: 0; display: grid; grid-template-columns: repeat(5, 1fr); }
.mpc-bg div { border-right: 1px solid var(--mpc-border-soft); }
.mpc-bg div:last-child { border-right: 0; }
.mpc-bg .out { background: var(--mpc-soft); }
.mpc-bg .today { background: color-mix(in srgb, var(--mp-dev) 9%, transparent); }
.mpc-week.is-focus { background: color-mix(in srgb, var(--mp-dev) 7%, transparent); box-shadow: inset 0 0 0 2px var(--mp-dev); }
.mpc-week.is-focus .mpc-bg .out { background: transparent; }
.mpc-focus-tag { position: absolute; right: 6px; top: 5px; z-index: 2; font-size: 11px; font-weight: 700; color: #fff; background: var(--mp-dev); border-radius: var(--mpc-r-xs); padding: 0 6px; line-height: 1.7; }
.mpc-fg { position: relative; padding-bottom: 6px; }
.mpc-g5 { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); }
.mpc-dn { display: flex; align-items: center; gap: 4px; padding: 4px 6px 2px; min-width: 0; }
.mpc-dn button, .mpc-dn b { border: 0; background: transparent; font-weight: 600; font-size: 12.5px; width: 26px; height: 26px; border-radius: 50%; color: var(--mpc-text); display: inline-grid; place-items: center; }
.mpc:not(.is-ro) .mpc-dn button:hover { background: var(--mpc-hover); }
.mpc-dn.out button, .mpc-dn.out b { color: var(--mpc-faint); }
.mpc-dn.hol button, .mpc-dn.hol b, .mpc-dn .hname { color: var(--mpc-danger); }
.mpc-dn .hname { font-size: 11px; }
.mpc-dn.today button, .mpc-dn.today b { background: var(--mp-dev); color: #fff; }
.mpc-ms-cell { display: flex; flex-direction: column; gap: 3px; padding: 0 4px; min-width: 0; }
.mpc-ms { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 3px 5px; border: 0; text-align: left; padding: 2px 6px; border-radius: var(--mpc-r-sm); font-size: 12px; font-weight: 700; line-height: 1.4;
          background: color-mix(in srgb, var(--ph) 16%, var(--mpc-surface)); color: var(--mpc-heading); min-width: 0; }
.mpc-ms::before { content: ""; flex: none; width: 8px; height: 8px; margin-top: 4px; transform: rotate(45deg); background: var(--ph); border-radius: var(--mpc-r-xs); }
.mpc-ms span { word-break: keep-all; overflow-wrap: anywhere; min-width: 0; }
.mpc-ms.ghost { background: transparent; color: var(--mpc-faint); font-weight: 500; text-decoration: line-through; outline: 1px dashed var(--mpc-border-strong); }
.mpc-ms.ghost::before { background: transparent; outline: 1.5px solid var(--mpc-faint); }
.mpc-lane { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); grid-template-rows: auto 7px; margin-top: 3px; }
.mpc-bar { --pg: 0%; --pb: 18%; grid-row: 1; margin: 0 3px; border: 0; border-left: 3px solid var(--ph); border-radius: var(--mpc-r-sm);
           background: linear-gradient(90deg, color-mix(in srgb, var(--ph) calc(var(--pb) + 22%), var(--mpc-surface)) var(--pg), color-mix(in srgb, var(--ph) var(--pb), var(--mpc-surface)) var(--pg));
           color: var(--mpc-heading); font-size: 12px; font-weight: 600; text-align: left; padding: 3px 6px; line-height: 1.4; display: flex; flex-wrap: wrap; align-items: center; gap: 2px 6px; min-width: 0; }
.mpc:not(.is-ro) .mpc-bar:hover, .mpc:not(.is-ro) .mpc-ms:hover { filter: brightness(.97); }
.mpc-bar.cl { border-top-left-radius: 0; border-bottom-left-radius: 0; margin-left: 0; border-left-style: dotted; }
.mpc-bar.cr { border-top-right-radius: 0; border-bottom-right-radius: 0; margin-right: 0; }
.mpc-bar.done { opacity: .55; }
.mpc-bar.pj { --pb: 9%; border-left-style: dashed; }
.mpc-bar .cont { color: var(--mpc-muted); font-weight: 500; font-size: 11px; }
.mpc-bar .t { word-break: keep-all; overflow-wrap: anywhere; min-width: 0; }
.mpc-bar .tag, .mpc-ms .tag { font-weight: 700; color: var(--mpc-muted); font-size: 11px; margin-right: 4px; }
.mpc-bar .tag { margin-right: 0; }
.mpc-base { grid-row: 2; margin: 2px 3px 0; border-top: 2px dashed color-mix(in srgb, var(--ph) 70%, transparent); height: 0; position: relative; }
.mpc-base.el::before, .mpc-base.er::after { content: ""; position: absolute; top: -5px; width: 2px; height: 7px; background: color-mix(in srgb, var(--ph) 70%, transparent); }
.mpc-base.el::before { left: 0; } .mpc-base.er::after { right: 0; }
.mpc-ppl { display: inline-flex; flex-wrap: wrap; gap: 2px 4px; }
.mpc-ob { background: #fff; display: inline-block; font-size: 11px; padding: 0 7px; border-radius: var(--adm-radius-md, .25rem); font-weight: 600; white-space: nowrap; line-height: 1.7; }
.mpc-late { display: inline-flex; border-radius: var(--mpc-r-xs); padding: 0 6px; font-size: 11px; font-weight: 700; white-space: nowrap; line-height: 1.6;
            background: color-mix(in srgb, var(--mpc-danger) 14%, transparent); color: var(--mpc-danger); }
.mpc-late.early { background: color-mix(in srgb, var(--mp-open) 16%, transparent); color: var(--mp-open); }
.mpc-late.over { background: var(--mpc-danger); color: #fff; }
.mpc-empty { padding: 28px 16px; text-align: center; color: var(--mpc-muted); }
@media print { .mpc-nav, .mpc-mode { display: none; } .mpc-scroll { overflow: visible; } .mpc-cal { min-width: 0; } .mpc-week { break-inside: avoid; }
               .mpc * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

export default class MasterCalendar {
    constructor() {
        this._styled = false;
    }

    /** 보고서 프린트 새 창에 실을 CSS */
    cssText() {
        return CSS;
    }

    mount(el, opts = {}) {
        this._injectStyle();
        const $el = $(el);
        const prev = $el.data('mpcState') || {};
        const today = opts.today || iso(new Date());
        const st = {
            ...prev,
            ...opts,
            today,
            hol: opts.holidays || prev.hol || {},
            PH: opts.phases || prev.PH || {},
            tasks: opts.tasks || prev.tasks || [],
            cursor: prev.cursor || ((opts.month || today.slice(0, 7)) + '-01'),
        };
        $el.data('mpcState', st);

        if (!prev.bound) {
            st.bound = true;
            $el.on('click', '[data-mv]', (e) => {
                const s = $el.data('mpcState');
                const v = +e.currentTarget.dataset.mv, d = parse(s.cursor);
                const home = s.focus ? s.focus[1] : s.today;
                s.cursor = v === 0 ? home.slice(0, 8) + '01' : iso(new Date(d.getFullYear(), d.getMonth() + v, 1));
                this._render($el);
            });
            $el.on('click', '[data-color]', (e) => {
                const s = $el.data('mpcState');
                s.colorBy = e.currentTarget.dataset.color;
                if (s.onColor) s.onColor(s.colorBy);
                this._render($el);
            });
            $el.on('click', '[data-id]', (e) => { const s = $el.data('mpcState'); if (!s.readonly && s.onOpen) s.onOpen(+e.currentTarget.dataset.id); });
            $el.on('click', '[data-new]', (e) => { const s = $el.data('mpcState'); if (!s.readonly && s.onNew) s.onNew(e.currentTarget.dataset.new); });
        }
        this._render($el);
    }

    /* ── 내부 ───────────────────────────────────────── */

    _injectStyle() {
        if (this._styled || document.getElementById('mpcCss')) { this._styled = true; return; }
        $('<style id="mpcCss">').text(CSS).appendTo('head');
        this._styled = true;
    }

    _render($el) {
        const s = $el.data('mpcState');
        const isBiz = d => { const w = parse(d).getDay(); return w !== 0 && w !== 6 && !s.hol[d]; };
        const bdiff = (a, b) => { if (!a || !b || a === b) return 0; const sg = b > a ? 1 : -1; let n = 0, c = a; while (c !== b) { c = addD(c, sg); if (isBiz(c)) n++; } return n * sg; };
        const mode = s.colorBy || 'project';
        const person = mode === 'person';
        const keyOf = t => t.color_key || (t.pj_num ? 'p' + t.pj_num : null);
        const pjIds = [...new Set(s.tasks.map(keyOf).filter(Boolean))].sort();
        const pjColor = k => (k ? PJ_PAL[pjIds.indexOf(k) % PJ_PAL.length] : PJ_NONE);
        const primary = t => t.phases.find(p => s.PH[p]?.kind === 'range') || t.phases[0] || 'etc';
        const color = t => (mode === 'phase' ? { cls: 'ph-' + primary(t), style: '' }
            : { cls: '', style: `--ph:${person ? pastel(t.owners[0]?.name)[1] : pjColor(keyOf(t))};` });
        const people = t => (person ? '' : `<span class="mpc-ppl">${t.owners.map(o => ob(o.name)).join('')}</span>`);
        const tag = t => (t.is_project ? '[프로젝트]' : `[${t.phases.map(p => s.PH[p]?.short).join('·')}]`);   // 단계 말머리 — 막대·◆ 공통
        const ownerTxt = t => t.owners.map(o => o.name).join(', ') || '미지정';
        const range = t => (t.is_ms ? fmt(t.mt_cur_end) : `${fmt(t.mt_cur_start)} ~ ${fmt(t.mt_cur_end)}`);
        const baseRange = t => (t.is_ms ? fmt(t.mt_plan_end) : `${fmt(t.mt_plan_start)} ~ ${fmt(t.mt_plan_end)}`);
        const flags = t => {
            const dl = bdiff(t.mt_plan_end, t.mt_cur_end);
            let h = '';
            if (dl > 0) h += `<span class="mpc-late" title="기준 ${esc(baseRange(t))} → 현재 ${esc(range(t))} (주말·공휴일 제외)">+${dl}일 · 원래 ${md(t.mt_plan_end)}</span>`;
            else if (dl < 0) h += `<span class="mpc-late early">${-dl}일 단축 · 원래 ${md(t.mt_plan_end)}</span>`;
            if (t.mt_status !== '완료' && t.mt_cur_end < s.today) h += '<span class="mpc-late over">기한 초과</span>';
            return h;
        };
        // 보고서에선 누를 일이 없으니 button 대신 div 로 — 커서·포커스가 생기지 않게
        const tagB = s.readonly ? 'div' : 'button type="button"';
        const tagE = s.readonly ? 'div' : 'button';

        const first = s.cursor, fd = parse(first);
        const y = fd.getFullYear(), m = fd.getMonth();
        const last = iso(new Date(y, m + 1, 0));
        const fw = fd.getDay();
        const start = fw === 6 ? addD(first, 2) : fw === 0 ? addD(first, 1) : addD(first, -(fw - 1));
        const end = addD(last, -((parse(last).getDay() + 6) % 7));
        const msOn = (d, i, v) => (i === 4 ? (v >= d && v <= addD(d, 2)) : v === d);   // 금요일 칸이 주말까지 받는다
        const wkLbl = v => { const w = parse(v).getDay(); return w === 6 ? ' (토)' : w === 0 ? ' (일)' : ''; };
        const ranges = s.tasks.filter(t => !t.is_ms);
        const mss = s.tasks.filter(t => t.is_ms);

        let weeks = '';
        for (let ws = start; ws <= end; ws = addD(ws, 7)) {
            const days = [...Array(5)].map((_, i) => addD(ws, i));
            const we = days[4];
            const out = d => d.slice(0, 7) !== first.slice(0, 7);
            // 강조 — 보고서는 보고 주차(토~금) 한 줄, 입력 화면은 오늘 하루
            const isToday = d => !s.focus && d === s.today;
            const isFocus = !!s.focus && ws <= s.focus[1] && we >= s.focus[0];
            const bg = days.map(d => `<div class="${out(d) ? 'out' : ''} ${isToday(d) ? 'today' : ''}"></div>`).join('');
            const dn = days.map(d => {
                const h = s.hol[d];
                const num = s.readonly ? `<b>${parse(d).getDate()}</b>` : `<button type="button" data-new="${d}" title="${fmt(d)} 작업 추가">${parse(d).getDate()}</button>`;
                return `<div class="mpc-dn ${out(d) ? 'out' : ''} ${h ? 'hol' : ''} ${isToday(d) ? 'today' : ''}">${num}${h ? `<span class="hname">${h}</span>` : ''}</div>`;
            }).join('');
            const msRow = days.map((d, i) => {
                const gh = mss.filter(t => t.mt_plan_end !== t.mt_cur_end && msOn(d, i, t.mt_plan_end)).map(t => {
                    const c = color(t);
                    return `<${tagB} class="mpc-ms ghost ${c.cls}" style="${c.style}" data-id="${t.num}" title="원래 일정 → ${fmt(t.mt_cur_end)}로 변경"><span><span class="tag">${tag(t)}</span>${esc(t.mt_title)} (원래${wkLbl(t.mt_plan_end)})</span></${tagE}>`;
                }).join('');
                const cur = mss.filter(t => msOn(d, i, t.mt_cur_end)).map(t => {
                    const c = color(t);
                    return `<${tagB} class="mpc-ms ${c.cls}" style="${c.style}" data-id="${t.num}" title="${esc(t.mt_title)} · ${esc(ownerTxt(t))}"><span><span class="tag">${tag(t)}</span>${esc(t.mt_title)}${wkLbl(t.mt_cur_end)}</span>${people(t)}${flags(t)}</${tagE}>`;
                }).join('');
                return `<div class="mpc-ms-cell">${gh}${cur}</div>`;
            }).join('');

            // 기간 막대 — 기준·현재 중 넓은 쪽으로 자리를 잡아 같은 줄(lane)에 겹치지 않게 쌓는다
            // 완료 막대는 진행 중인 막대 아래 줄로 따로 모은다 — 위쪽이 지금 할 일이 되게
            const items = ranges.map(t => ({ t, done: t.mt_status === '완료', s: mn(t.mt_plan_start, t.mt_cur_start), e: mx(t.mt_plan_end, t.mt_cur_end) }))
                .filter(o => o.s <= we && o.e >= ws)
                .sort((a, b) => (a.done !== b.done ? (a.done ? 1 : -1) : a.s < b.s ? -1 : a.s > b.s ? 1 : (a.e > b.e ? -1 : 1)));
            const lanes = [];
            for (const o of items) {
                const cs = mx(o.s, ws), ce = mn(o.e, we);
                let l = lanes.find(x => x.done === o.done && x.end < cs);
                if (!l) { l = { end: '', done: o.done, items: [] }; lanes.push(l); }
                l.end = ce; l.items.push(o);
            }
            const span = (a, b) => {   // 주말에 걸친 끝은 월·금으로 접는다
                const ca = Math.min(5, Math.max(1, diff(ws, a) + 1)), cb = Math.min(5, Math.max(1, diff(ws, b) + 1));
                return ca > cb ? null : `grid-column:${ca}/${cb + 1}`;
            };
            const laneHtml = lanes.map(l => `<div class="mpc-lane">${l.items.map(({ t }) => {
                let h = '';
                const c = color(t);
                if (t.mt_cur_start <= we && t.mt_cur_end >= ws) {
                    const g = span(mx(t.mt_cur_start, ws), mn(t.mt_cur_end, we));
                    // 진행률 칠하기 — 기간 전체에서 진행률만큼 지난 지점이 이 주 조각의 몇 % 에 오는지
                    let pg = '';
                    if (t.mt_status !== '완료' && t.mt_progress > 0) {
                        const segS = mx(t.mt_cur_start, ws), segE = mn(t.mt_cur_end, we);
                        const reach = (diff(t.mt_cur_start, t.mt_cur_end) + 1) * Math.min(100, t.mt_progress) / 100;
                        const f = Math.max(0, Math.min(1, (reach - diff(t.mt_cur_start, segS)) / (diff(segS, segE) + 1)));
                        if (f > 0) pg = `--pg:${Math.round(f * 100)}%;`;
                    }
                    const tip = t.is_project
                        ? `${t.pg_name ? esc(t.pg_name) + ' › ' : ''}${esc(t.pj_name)} · ${esc(ownerTxt(t))} · ${esc(range(t))} · ${esc(t.mt_status)} ${t.mt_progress}% · 작업 없음 — 원장 기간으로 그림`
                        : `${t.pg_name ? esc(t.pg_name) + ' › ' : ''}${esc(t.mt_title)} · ${esc(ownerTxt(t))} · ${esc(range(t))} · ${esc(t.mt_status)} ${t.mt_progress}%`;
                    const cls = `${c.cls} ${t.mt_cur_start < ws ? 'cl' : ''} ${t.mt_cur_end > we ? 'cr' : ''} ${t.is_project ? 'pj' : ''}`;
                    // 이름은 시작하는 주(또는 이 달 첫 줄)에만, 지연 표시는 끝나는 주에만
                    const head = t.mt_cur_start >= ws || ws === start;
                    const body = head
                        ? `<span class="tag">${tag(t)}</span><span class="t">${t.mt_status === '완료' ? '✓ ' : ''}${esc(t.mt_title)}</span>${people(t)}`
                        : `<span class="cont">← ${esc(t.mt_title)}</span>`;   // 이어지는 주 — 이름만 흐리게 (말머리·담당자는 시작 주에)
                    if (g) h += `<${tagB} class="mpc-bar ${cls} ${t.mt_status === '완료' ? 'done' : ''}" style="${c.style}${pg}${g}" data-id="${t.num}" title="${tip}">${body}${t.mt_cur_end <= we ? flags(t) : ''}</${tagE}>`;
                }
                if ((t.mt_plan_start !== t.mt_cur_start || t.mt_plan_end !== t.mt_cur_end) && t.mt_plan_start <= we && t.mt_plan_end >= ws) {
                    const g = span(mx(t.mt_plan_start, ws), mn(t.mt_plan_end, we));
                    if (g) h += `<div class="mpc-base ${c.cls} ${t.mt_plan_start >= ws ? 'el' : ''} ${t.mt_plan_end <= we ? 'er' : ''}" style="${c.style}${g}" title="기준 일정 ${esc(baseRange(t))}"></div>`;
                }
                return h;
            }).join('')}</div>`).join('');

            weeks += `<div class="mpc-week ${isFocus ? 'is-focus' : ''}">${isFocus ? '<span class="mpc-focus-tag">보고 주차</span>' : ''}<div class="mpc-bg">${bg}</div><div class="mpc-fg"><div class="mpc-g5">${dn}</div><div class="mpc-g5">${msRow}</div>${laneHtml}</div></div>`;
        }

        const owners = [...new Map(s.tasks.flatMap(t => t.owners).map(o => [o.name, o])).values()];
        const pjName = k => { const t = s.tasks.find(x => keyOf(x) === k) || {}; return t.color_label || t.pj_name || ''; };
        const legend = mode === 'phase'
            ? Object.entries(s.PH).map(([k, v]) => `<span class="ph-${k}"><i class="${v.kind === 'ms' ? 'ms' : ''}"></i>${v.label}</span>`).join('')
            : (person
                ? owners.map(o => `<span>${ob(o.name)}</span>`).join('')
                : pjIds.map(id => `<span style="--ph:${pjColor(id)}"><i></i>${esc(pjName(id))}</span>`).join('')
                  + (s.tasks.some(t => !keyOf(t)) ? `<span style="--ph:${PJ_NONE}"><i></i>프로젝트 없음</span>` : ''))
              + '<span>[ ] 말머리 = 작업 구분</span>';
        const modeBtn = (v, label) => `<button type="button" data-color="${v}" class="${mode === v ? 'on' : ''}">${label}</button>`;
        const modeHtml = `<div class="mpc-mode">${modeBtn('project', '프로젝트별')}${modeBtn('phase', '구분별')}${modeBtn('person', '담당자별')}</div>`;

        $el.html(`<div class="mpc ${s.readonly ? 'is-ro' : ''}">
            <div class="mpc-head">
                <div class="mpc-nav">
                    <button type="button" data-mv="-1" aria-label="이전 달">‹</button>
                    <button type="button" data-mv="0">${s.focus ? '이번주' : '이번달'}</button>
                    <button type="button" data-mv="1" aria-label="다음 달">›</button>
                </div>
                <h5 class="mpc-title">${y}년 ${m + 1}월</h5>
                ${modeHtml}
                <div class="mpc-legend">${legend}<span><i class="base"></i>기준 일정(변경 전)</span><span>주말 일정은 금요일 칸</span></div>
            </div>
            ${s.tasks.length ? '' : `<div class="mpc-empty">${s.emptyHtml || '등록된 작업이 없습니다.'}</div>`}
            <div class="mpc-scroll"><div class="mpc-cal"><div class="mpc-dow">${[...'월화수목금'].map(d => `<div>${d}</div>`).join('')}</div>${weeks}</div></div>
        </div>`);
    }
}
